import type { Cell, Cue } from '@rundown-studio/types'
import type { GridColumn } from '../column/column.types'

import { defineDomSchema } from '@/utils/dom-schema'

/**
 * Unique identifier for a cell, available even when `cell` is undefined
 */
export type GridCellKey = `cell_${Cue['id']}_${GridColumn['id']}`

/**
 * Cell model.
 *
 * Can be undefined, `Cell` is only created if user edits the content
 */
export type GridCell = Cell | undefined

/**
 * Discriminated union of Cell content
 *
 * @note Does not include the two deprecated `ColumnType`s.
 */
export type GridCellContent =
  | { content: RichtextCellContent }
  | { content: SelectCellContent }

type RichtextCellContent = {
  text: string
}

type SelectCellContent = {
  selected: string
}

/**
 * Type guard that safely checks whether Cell's content is of type 'text' or not.
 *
 * @note `text` is not actually plain text, but rather a HTML string.
 */
export function isRichtextContent(
  cellContent: unknown,
): cellContent is RichtextCellContent {
  return (
    typeof cellContent === 'object' &&
    cellContent !== null &&
    'text' in cellContent &&
    typeof (cellContent as Record<string, unknown>).text === 'string'
  )
}

/**
 * Type guard that safely checks whether Cell's content is of type 'select' or not.
 */
export function isSelectContent(
  cellContent: unknown,
): cellContent is SelectCellContent {
  return (
    typeof cellContent === 'object' &&
    cellContent !== null &&
    'selected' in cellContent &&
    typeof (cellContent as Record<string, unknown>).selected === 'string'
  )
}

export type CellDomSchema = {
  'data-cell-key': GridCellKey
  'aria-selected': boolean
}

export type CellDomSchemaKey = `[${keyof CellDomSchema}]`

export const CellDomUtils = defineDomSchema<CellDomSchema, HTMLDivElement>()
